import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useAdPlacement, useSiteSettings } from '@/hooks/useSiteSettings';

interface AdSlotProps {
  slotId: string;
  className?: string;
  format?: 'horizontal' | 'vertical' | 'rectangle';
}

/**
 * AdSlot component that conditionally renders ads based on CMS settings.
 * Falls back to AdSense auto display ad if publisher ID is configured
 * but no specific placement exists in the database.
 */
export function AdSlot({ slotId, className, format = 'horizontal' }: AdSlotProps) {
  const { placement, isLoading } = useAdPlacement(slotId);
  const { data: siteSettings } = useSiteSettings();
  const adContainerRef = useRef<HTMLDivElement>(null);
  const adInserted = useRef(false);

  const hasPublisherId = !!(siteSettings?.adsense_publisher_id?.trim());

  // Determine render mode
  const renderMode = (() => {
    if (isLoading) return 'none';

    if (placement?.is_enabled) {
      const hasCustomCode = !!(placement.custom_code?.trim());
      const hasAdSenseSlot = !!(placement.adsense_slot_id?.trim());
      const hasAdSense = hasAdSenseSlot && hasPublisherId;
      if (hasCustomCode || hasAdSense) return 'placement';
    }

    // Fallback to auto AdSense
    if (hasPublisherId) return 'auto';

    return 'none';
  })();

  // Reset on placement change
  useEffect(() => {
    adInserted.current = false;
    if (adContainerRef.current) {
      adContainerRef.current.innerHTML = '';
    }
  }, [placement?.id, renderMode]);

  // Inject ad
  useEffect(() => {
    if (!adContainerRef.current || adInserted.current) return;
    if (renderMode === 'none') return;

    const container = adContainerRef.current;

    if (renderMode === 'placement' && placement) {
      // Custom HTML code
      if (placement.custom_code?.trim()) {
        container.innerHTML = placement.custom_code;
        const scripts = container.querySelectorAll('script');
        scripts.forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode?.replaceChild(newScript, oldScript);
        });
        adInserted.current = true;
        return;
      }

      // AdSense with specific slot
      if (placement.adsense_slot_id && hasPublisherId) {
        const ins = document.createElement('ins');
        ins.className = 'adsbygoogle';
        ins.style.display = 'block';
        ins.setAttribute('data-ad-client', siteSettings!.adsense_publisher_id!);
        ins.setAttribute('data-ad-slot', placement.adsense_slot_id);
        ins.setAttribute('data-ad-format', (placement as any).adsense_format || 'auto');
        ins.setAttribute('data-full-width-responsive', 'true');
        if ((placement as any).adsense_layout) {
          ins.setAttribute('data-ad-layout', (placement as any).adsense_layout);
        }
        if ((placement as any).adsense_layout_key) {
          ins.setAttribute('data-ad-layout-key', (placement as any).adsense_layout_key);
        }
        container.appendChild(ins);
        try {
          ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
        } catch (e) {
          console.error('AdSense error:', e);
        }
        adInserted.current = true;
        return;
      }
    }

    // Auto fallback
    if (renderMode === 'auto' && hasPublisherId) {
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.setAttribute('data-ad-client', siteSettings!.adsense_publisher_id!);
      ins.setAttribute('data-ad-format', 'auto');
      ins.setAttribute('data-full-width-responsive', 'true');
      container.appendChild(ins);
      try {
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
      } catch (e) {
        console.error('AdSense auto error:', e);
      }
      adInserted.current = true;
    }
  }, [renderMode, placement, siteSettings, hasPublisherId]);

  if (renderMode === 'none') return null;

  const formatClasses = {
    horizontal: 'min-h-[90px] md:min-h-[120px]',
    vertical: 'w-full min-h-[600px]',
    rectangle: 'aspect-square max-w-[300px]',
  };

  return (
    <div className={cn('relative', className)} data-ad-slot={slotId}>
      <div className="flex items-center justify-center gap-2 mb-2">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Oglas</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <div
        ref={adContainerRef}
        className={cn('ad-slot overflow-hidden', formatClasses[format])}
      />
    </div>
  );
}
